import { circle, proj, self, skill, stage } from '../../content/classes/kit';
import { registry } from '../../sim/registry';
import { resolveSkill } from '../../sim/skills';
import type { ResolvedSkill, SkillDef } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import type { ActionKey } from '../../sim/types';
import {
  CANNON_RANGE,
  CANNON_SPEED,
  FULL_SAIL_SPEED,
  FULL_SAIL_STATUS,
  PATCH_FRACTION,
  PATCH_STATUS,
  SKILL_CANNON,
  SKILL_FULL_SAIL,
  SKILL_PATCH,
} from './data';
import type { ShipStats } from './ship';

/**
 * Ship abilities are ordinary skills (so casts, cooldowns, projectiles and
 * statuses replicate like any other) cast by the ship actor. Their statuses
 * carry no stat mods: the sailing controller reads them.
 */

const FULL_SAIL_SECONDS = 6;
const PATCH_SECONDS = 4;

export const SHIP_SKILL_DEFS: readonly SkillDef[] = [
  skill({
    id: SKILL_FULL_SAIL,
    name: 'Full Sail',
    description: `Crowd on every scrap of canvas: top speed ×${FULL_SAIL_SPEED} and double acceleration for ${FULL_SAIL_SECONDS} s.`,
    icon: 'dash',
    castType: 'normal',
    cooldown: 18,
    mana: 0,
    hue: 0x7ad8ff,
    stages: [stage('Idle', 0.15, [self(0, { id: FULL_SAIL_STATUS, duration: FULL_SAIL_SECONDS })], { cancelAt: 0 })],
  }),
  skill({
    id: SKILL_CANNON,
    name: 'Cannon Volley',
    description: 'Fire three cannonballs toward the cursor. A volley that lands near wreckage blasts its cargo loose from range.',
    icon: 'orb',
    castType: 'normal',
    cooldown: 6,
    mana: 0,
    hue: 0xffa040,
    stages: [
      stage(
        'Idle',
        0.3,
        [
          proj(0.05, {
            speed: CANNON_SPEED,
            range: CANNON_RANGE,
            radius: 0.7,
            count: 3,
            spreadDeg: 14,
            height: 2.2,
            hit: { shape: circle(0.7), coef: 1.6, reaction: { kind: 'flinch' } },
            explode: { shape: circle(2.2), coef: 1.1, vfx: 'heavy' },
          }),
        ],
        { cancelAt: 0.1 },
      ),
    ],
  }),
  skill({
    id: SKILL_PATCH,
    name: 'Patch the Hull',
    description: `The crew plugs the leaks: restores ${Math.round(PATCH_FRACTION * 100)}% of max hull over ${PATCH_SECONDS} s.`,
    icon: 'shield',
    castType: 'normal',
    cooldown: 45,
    mana: 0,
    hue: 0x9be07a,
    stages: [stage('Idle', 0.2, [self(0, { id: PATCH_STATUS, duration: PATCH_SECONDS })], { cancelAt: 0 })],
  }),
];

/** Keys that work at the helm (Space doubles as Full Sail, like fast sailing). */
export const SHIP_KEYS: Partial<Record<ActionKey, string>> = { Q: SKILL_FULL_SAIL, W: SKILL_CANNON, E: SKILL_PATCH, SPACE: SKILL_FULL_SAIL };
/** Skill bar (Q W E R A S D F) while sailing. */
export const SHIP_BAR: readonly (string | null)[] = [SKILL_FULL_SAIL, SKILL_CANNON, SKILL_PATCH, null, null, null, null, null];

let registered = false;

/** Registers the ship skills and statuses (idempotent; every peer needs them to replay casts). */
export function registerShipContent(): void {
  if (registered) return;
  registered = true;
  for (const def of SHIP_SKILL_DEFS) registry.registerSkill(def);
  registerStatus({ id: FULL_SAIL_STATUS, name: 'Full Sail', kind: 'buff', icon: FULL_SAIL_STATUS, description: `Top speed ×${FULL_SAIL_SPEED}, acceleration doubled.`, stacking: 'refresh', color: 0x7ad8ff });
  registerStatus({ id: PATCH_STATUS, name: 'Patching', kind: 'buff', icon: PATCH_STATUS, description: 'The crew is patching the hull.', stacking: 'refresh', color: 0x9be07a });
}

/** The ship's skills resolved for a crew (the gunner shortens the cannon's cooldown). */
export function resolveShipSkills(stats: Pick<ShipStats, 'cannonCdr'>): ResolvedSkill[] {
  return SHIP_SKILL_DEFS.map((def) => {
    const s = resolveSkill(def, 1, []);
    if (s.id === SKILL_CANNON) s.cooldown *= 1 - stats.cannonCdr;
    return s;
  });
}

export const PATCH_DURATION = PATCH_SECONDS;
